import type { DiscoveryEntry } from './discovery';
import type { QuestionType } from '@/lib/analytics';
import type { Content, ImageField, KeyTextField, RichTextField } from '@prismicio/client';

/**
 * The chat engine's vocabulary: a **script** built from a topic's slices, the
 * **messages** it lands in the thread, the **prompt** it puts in the answer
 * control, and the **effects** it asks the caller to perform.
 *
 * Nothing here knows about React, Prismic reads, timers or the user store —
 * `reducer.ts` is a pure function over these types, which is what makes the
 * whole flow testable from a fixture.
 */

// --- Script ------------------------------------------------------------------

/** A picture with its caption and long description — the image sheet's content. */
export type ChatImage = {
  image: ImageField;
  caption: KeyTextField;
  description: RichTextField;
};

/**
 * One playable unit of a module, in order. A step maps 1:1 to an *enabled*
 * slice that the app renders, so `script.steps.length` is the module's
 * `nSlices` and every step completing is one `completeSlice` call.
 */
export type ChatStep =
  | { kind: 'coach'; id: string; text: RichTextField }
  | { kind: 'image'; id: string; image: ChatImage }
  | { kind: 'carousel'; id: string; images: Array<ChatImage> }
  | {
      kind: 'video';
      id: string;
      title: KeyTextField;
      subtitle: KeyTextField;
      poster: ImageField;
      embedLink: KeyTextField;
      transcript: RichTextField;
    }
  | {
      kind: 'quiz';
      id: string;
      questionId: string;
      question: RichTextField;
      /** 1-based, exactly as `accepted_answer` is authored. */
      acceptedAnswer: number | null;
      answers: Array<{ label: RichTextField; feedback: RichTextField }>;
    }
  | {
      kind: 'checklist';
      id: string;
      questionId: string;
      question: RichTextField;
      /** Said once the ticked set is right. Blank falls back to the catalogue's "That's right". */
      responseText: RichTextField;
      /** Said on every wrong set. Blank falls back to the catalogue's "Not quite". */
      feedbackWrong: RichTextField;
      /** `feedback` explains the item when the learner gets it wrong. */
      items: Array<{ label: RichTextField; correct: boolean; feedback: RichTextField }>;
    }
  | {
      kind: 'interaction';
      id: string;
      questionId: string;
      question: KeyTextField;
      options: Array<{ label: KeyTextField; feedback: RichTextField }>;
    }
  /**
   * A set the learner works through at their own pace: one entry at a time,
   * each taken off the list as it is seen. One step, however many entries —
   * it finishes when the list is empty.
   */
  | {
      kind: 'discovery';
      id: string;
      questionId: string;
      /** The coach message that introduces the set, authored on the slice. */
      intro: RichTextField;
      /** The instruction over the buttons, and the one used from the second pick on. */
      prompt: KeyTextField;
      promptAgain: KeyTextField;
      /** Counter template, e.g. `{done} of {total} sharks explored`. Blank hides it. */
      progressLabel: KeyTextField;
      outro: RichTextField;
      entries: Array<DiscoveryEntry>;
    }
  | { kind: 'divider'; id: string; index: number; title: KeyTextField }
  | { kind: 'takeaways'; id: string; heading: KeyTextField; points: Array<KeyTextField> }
  /** An `activity` slice, played as a card, or in the footer for `free_text`. */
  | { kind: 'activity'; id: string; questionId: string; slice: Content.ActivitySlice };

/** A checkpoint segment in the module header, one per `section_divider`. */
export type Checkpoint = {
  /** 1-based, and the `checkpoint_index` reported to GA4. */
  index: number;
  title: string;
};

/** What the completion card and the analytics payloads need from the route. */
export type ScriptMeta = {
  /** Stable across languages — the topic's `module_key`. */
  moduleKey: string;
  /** 1-based position of this module in the learner's journey. */
  moduleIndex: number;
  moduleTotal: number;
  title: string;
  /** Title of the next module, or `null` when this was the last one. */
  upNext: string | null;
};

export type ChatScript = {
  meta: ScriptMeta;
  steps: Array<ChatStep>;
  checkpoints: Array<Checkpoint>;
};

// --- Messages ----------------------------------------------------------------

/**
 * A landed message. `id` is stable for the life of the run, which is what
 * `MessageScrollerItem` anchors on.
 */
export type ChatMessage =
  /**
   * Plain text where the slice authors it that way (`user_interaction`).
   * `prompt` marks the question a step is asking, which the thread sets in bold.
   */
  | { id: string; kind: 'coach'; text: RichTextField | string; prompt?: boolean }
  /**
   * The learner's own words. A picture answer whose asset carries no alt text
   * has none, so the reducer reports its 1-based position instead and the
   * renderer names it from the catalogue — the reducer holds no strings.
   */
  | { id: string; kind: 'user'; text: string; optionNumber?: number }
  /**
   * `text` is the authored feedback; `null` falls back to a catalogue string.
   * `reveal` marks the bubble that hands over an activity's answer when the
   * learner asks for it. `hint` marks the short bubble after a repeat miss that
   * points at the card's hint.
   */
  | { id: string; kind: 'feedback'; correct: boolean; text: RichTextField | null; reveal?: boolean; hint?: boolean }
  | { id: string; kind: 'image'; image: ChatImage }
  | { id: string; kind: 'carousel'; images: Array<ChatImage> }
  | {
      id: string;
      kind: 'video';
      title: KeyTextField;
      subtitle: KeyTextField;
      poster: ImageField;
      embedLink: KeyTextField;
      transcript: RichTextField;
    }
  | { id: string; kind: 'marker'; index: number; title: KeyTextField }
  /** "3 of 6 sharks explored" — the receipt a discovery leaves after each pick. */
  | { id: string; kind: 'discovered'; text: string; done: number; total: number }
  | { id: string; kind: 'takeaways'; heading: KeyTextField; points: Array<KeyTextField> }
  /**
   * An activity card. `answered` freezes it as a record of what the learner
   * did; `revealed` additionally draws the answer on it, which is where
   * "Show me the answer" lands.
   */
  | { id: string; kind: 'activity'; slice: Content.ActivitySlice; answered: boolean; revealed: boolean }
  | { id: string; kind: 'completion'; score: number; moduleIndex: number; moduleTotal: number; upNext: string | null };

/** Message kinds the coach "types" — the ones that get a typing indicator. */
export const TYPED_MESSAGE_KINDS = new Set<ChatMessage['kind']>(['coach', 'feedback']);

// --- Prompts -----------------------------------------------------------------

/** What the answer control is showing, keyed by `kind`. */
export type Prompt =
  /** `user_interaction` — wrapping pills, any option advances, never scored. */
  | { kind: 'quick'; stepId: string; questionId: string; questionIndex: number; options: Array<PromptOption> }
  /** `quiz` — stacked full-width buttons, one correct answer, retry until right. */
  | { kind: 'single'; stepId: string; questionId: string; questionIndex: number; options: Array<PromptOption> }
  /** `text_checklist` — toggle chips plus a confirm; correct is an exact set. */
  | {
      kind: 'multi';
      stepId: string;
      questionId: string;
      questionIndex: number;
      /** Confirm is disabled below this many picks. */
      min: number;
      options: Array<PromptOption>;
    }
  /**
   * `discovery` — the entries still to be seen. The one prompt that re-arms
   * itself: answering it removes the entry chosen and hands back the rest,
   * and the step only finishes when there are none left.
   */
  | {
      kind: 'discover';
      stepId: string;
      questionId: string;
      questionIndex: number;
      /** The instruction over the buttons for *this* round. */
      heading: KeyTextField;
      options: Array<PromptOption>;
      /** Seen so far, and how many there are in total — for the footer's count. */
      done: number;
      total: number;
    }
  /** `video` — the player dialog's Continue button is the only way on. */
  | { kind: 'gate'; stepId: string }
  /** Ticket 07: `activity` variation `free_text`. */
  | {
      kind: 'text';
      stepId: string;
      questionId: string;
      questionIndex: number;
      placeholder: KeyTextField;
      maxLength: number;
    }
  /** Ticket 07: the interactive activity card marks itself and reports back. */
  | {
      kind: 'activity';
      stepId: string;
      questionId: string;
      questionIndex: number;
      slice: Content.ActivitySlice;
    };

export type PromptOption = {
  id: string;
  /** Rich text for quiz and checklist options, plain text for quick replies. */
  label: RichTextField | KeyTextField;
  correct: boolean;
};

/** What the answer control sends back. The `kind` must match `pending.kind`. */
export type AnswerPayload =
  | { kind: 'quick'; optionId: string }
  | { kind: 'single'; optionId: string }
  | { kind: 'multi'; optionIds: Array<string> }
  | { kind: 'discover'; optionId: string }
  | { kind: 'gate' }
  | { kind: 'text'; value: string }
  /**
   * Activities mark themselves, so the reducer only records the verdict.
   * `mistake` is the item a wrong check blamed, as in `ActivityVerdict`.
   */
  | { kind: 'activity'; correct: boolean; receipt: string; mistake?: number };

// --- Events ------------------------------------------------------------------

export type ChatEvent =
  | { type: 'start'; script: ChatScript }
  /** The pacing layer's tick: land the next message, or move to the next step. */
  | { type: 'advance' }
  | { type: 'answer'; payload: AnswerPayload }
  /** Re-arms the current prompt after wrong-answer feedback has landed. */
  | { type: 'retry' }
  /** Ticket 07: gives up on an activity, shows the answer and moves on. */
  | { type: 'reveal' };

// --- Effects -----------------------------------------------------------------

/**
 * Side effects the reducer asks for but never performs: the store drains this
 * queue and calls the user store and `track()`. Keeping them as data is what
 * lets the tests assert on counters and analytics without mocking either.
 */
export type ChatEffect =
  /** One enabled slice finished — `completeSlice(moduleKey)`. */
  | { kind: 'slice_completed'; stepId: string }
  /**
   * One answer attempt. `scores` is false for `user_interaction` and free
   * text, which report to GA4 but never touch `nTried`/`nAnswered`.
   */
  | {
      kind: 'attempt';
      questionId: string;
      questionIndex: number;
      questionType: QuestionType;
      correct: boolean;
      /** 1 for the first go, 2 for the retry, and so on. */
      attempt: number;
      scores: boolean;
    }
  /** The module is done — `finishModule(moduleKey, score)` and `topic_complete`. */
  | { kind: 'module_completed'; score: number };
